// Utility functions for tooltip functionality in academic articles

// TypeScript interfaces for tooltip system
export interface TooltipOptions {
  type: 'hover' | 'pinned'
  text: string
  targetElement: HTMLElement
}

export interface TooltipState {
  type: 'hover' | 'pinned'
  element: HTMLElement
  targetElement: HTMLElement
  text: string
  timer?: number
  resizeObserver?: ResizeObserver
  scrollHandler?: { target: EventTarget, listener: () => void }
}

// Both variants are portalled to <body> with position: fixed so they can use
// the full viewport horizontally — sitting inside the article scroller confined
// pinned tooltips to the narrow reading column and clipped them at its edges
// (overflow-y:auto also clips x per CSS spec). z-[50] keeps them above sibling
// panes (aside/reference) which may create their own stacking contexts.
// w-max forces width to max-content so both variants size purely off content.
const TOOLTIP_BASE_CLASSES = 'text-xs font-normal sm:text-sm sm:leading-6 shadow-sm rounded-md p-4 w-max min-w-[18rem] max-w-[26rem] text-blurple-500'
const HOVER_TOOLTIP_CLASSES = 'z-[50] bg-blurple-50'
const PINNED_TOOLTIP_CLASSES = 'z-[50] bg-blurple-50 pr-16'
const CLOSE_BUTTON_CLASSES = 'absolute top-4 right-4 text-secondary pb-[1px] text-body font-regular border border-black rounded-lg aspect-square w-8 hover:bg-surface-secondary active:bg-surface-invert active:text-white'

// Global tooltip state management
let currentTooltipState: TooltipState | null = null

export function createTooltip(options: TooltipOptions): HTMLElement {
  const { type, text, targetElement } = options

  const tooltipEl = document.createElement('div')
  const baseClasses = TOOLTIP_BASE_CLASSES
  const typeClasses = type === 'pinned' ? PINNED_TOOLTIP_CLASSES : HOVER_TOOLTIP_CLASSES

  tooltipEl.className = `${baseClasses} ${typeClasses}`
  tooltipEl.textContent = text
  // Both variants live on <body>; pinned tracks its target via scroll/resize
  // listeners set up in showTooltip so it still follows the article.
  tooltipEl.style.position = 'fixed'

  if (type === 'hover') {
    tooltipEl.style.pointerEvents = 'none'
  }

  // Position the tooltip
  positionTooltip(tooltipEl, targetElement, type)

  // Add close button for pinned tooltips
  if (type === 'pinned') {
    addCloseButton(tooltipEl, () => hideTooltip())
  }

  return tooltipEl
}

function findScrollContainer(el: HTMLElement): HTMLElement | null {
  let node: HTMLElement | null = el.parentElement
  while (node) {
    const style = getComputedStyle(node)
    if (style.overflowY === 'auto' || style.overflowY === 'scroll') {
      return node
    }
    node = node.parentElement
  }
  return null
}

// Keep the tooltip's centered x inside [halfWidth+edgePadding, containerWidth-halfWidth-edgePadding]
// so its full width fits inside the container it lives in (viewport for hover, scroller for pinned).
function clampCenter(desiredCenter: number, halfWidth: number, containerWidth: number, edgePadding: number): number {
  const minCenter = halfWidth + edgePadding
  const maxCenter = containerWidth - halfWidth - edgePadding
  if (minCenter > maxCenter) {
    return containerWidth / 2
  }
  return Math.min(Math.max(desiredCenter, minCenter), maxCenter)
}

// Hide the pinned tooltip when its target scrolls out of the article scroller's
// visible band — otherwise the tooltip would float in the middle of the sticky
// header or over unrelated layout.
function isTargetInScroller(target: HTMLElement): boolean {
  const scroller = findScrollContainer(target)
  if (!scroller) {
    return true
  }
  const scrollerRect = scroller.getBoundingClientRect()
  const rect = target.getBoundingClientRect()
  return rect.bottom > scrollerRect.top && rect.top < scrollerRect.bottom
}

export function positionTooltip(tooltip: HTMLElement, target: HTMLElement, type: 'hover' | 'pinned'): void {
  const rect = target.getBoundingClientRect()
  const spacing = 8
  const edgePadding = 8
  const placeBelow = rect.top + rect.height / 2 < window.innerHeight / 2

  // Attach to <body> before measuring so offsetWidth reflects rendered width.
  if (tooltip.parentNode !== document.body) {
    document.body.appendChild(tooltip)
  }

  if (type === 'pinned' && !isTargetInScroller(target)) {
    tooltip.style.visibility = 'hidden'
    return
  }
  tooltip.style.visibility = ''

  const halfWidth = tooltip.offsetWidth / 2
  const center = clampCenter(rect.left + rect.width / 2, halfWidth, window.innerWidth, edgePadding)
  tooltip.style.left = `${center}px`
  if (placeBelow) {
    tooltip.style.top = `${rect.bottom + spacing}px`
    tooltip.style.transform = 'translate(-50%, 0)'
  }
  else {
    tooltip.style.top = `${rect.top - spacing}px`
    tooltip.style.transform = 'translate(-50%, -100%)'
  }
}

// Reflow triggers we cannot control (sticky article headers expanding/collapsing,
// commentary panels opening, images loading) change the target's position within
// the scroller after pinning. Observe the chain from target up to the scroller so
// any size change reruns the anchor math.
function observeAnchors(target: HTMLElement, callback: () => void): ResizeObserver {
  const observer = new ResizeObserver(callback)
  const scroller = findScrollContainer(target) ?? document.body
  let node: HTMLElement | null = target
  while (node) {
    observer.observe(node)
    if (node === scroller) {
      break
    }
    node = node.parentElement
  }
  return observer
}

export function addCloseButton(tooltip: HTMLElement, onClose: () => void): void {
  const closeBtn = document.createElement('button')
  closeBtn.className = CLOSE_BUTTON_CLASSES
  closeBtn.innerHTML = '×'
  closeBtn.setAttribute('aria-label', 'Close tooltip')

  closeBtn.addEventListener('click', (e) => {
    e.preventDefault()
    e.stopPropagation()
    onClose()
  })

  tooltip.appendChild(closeBtn)
}

export function showTooltip(el: HTMLElement, text: string, type: 'hover' | 'pinned' = 'hover') {
  hideTooltip()

  const tooltipEl = createTooltip({ type, text, targetElement: el })

  let resizeObserver: ResizeObserver | undefined
  let scrollHandler: { target: EventTarget, listener: () => void } | undefined
  if (type === 'pinned') {
    el.classList.add('tooltip-pinned')
    const reposition = () => positionTooltip(tooltipEl, el, 'pinned')
    resizeObserver = observeAnchors(el, reposition)
    // Pinned tooltips live on <body>, so we have to manually re-anchor as the
    // article scrolls. Listen on the scroller (not window) since the page uses
    // a nested scroll container with body { overflow: hidden }.
    const scrollTarget: EventTarget = findScrollContainer(el) ?? window
    scrollTarget.addEventListener('scroll', reposition, { passive: true })
    scrollHandler = { target: scrollTarget, listener: reposition }
  }

  currentTooltipState = {
    type,
    element: tooltipEl,
    targetElement: el,
    text,
    resizeObserver,
    scrollHandler,
  }
}

export function setTooltipTimer(timerId: number): void {
  if (currentTooltipState) {
    currentTooltipState.timer = timerId
  }
}

export function hideTooltip() {
  if (currentTooltipState) {
    const { element, timer, targetElement, resizeObserver, scrollHandler } = currentTooltipState

    if (timer) {
      clearTimeout(timer)
    }

    resizeObserver?.disconnect()

    if (scrollHandler) {
      scrollHandler.target.removeEventListener('scroll', scrollHandler.listener)
    }

    if (element.parentNode) {
      element.parentNode.removeChild(element)
    }

    // Remove pinned class from target element
    targetElement.classList.remove('tooltip-pinned')

    currentTooltipState = null
  }
}

// Utility functions for tooltip state management
export function getCurrentTooltipState(): TooltipState | null {
  return currentTooltipState
}

export function isTooltipPinned(): boolean {
  return currentTooltipState?.type === 'pinned'
}

export function isTooltipPinnedForElement(element: HTMLElement): boolean {
  return currentTooltipState?.type === 'pinned' && currentTooltipState?.targetElement === element
}

export function toggleTooltipPin(el: HTMLElement, text: string): void {
  if (isTooltipPinnedForElement(el)) {
    hideTooltip()
  }
  else {
    hideTooltip()
    showTooltip(el, text, 'pinned')
  }
}

export function setAnchorAttrs(rootEl: Element) {
  rootEl.querySelectorAll('a').forEach((a) => {
    if (!(a instanceof HTMLAnchorElement)) {
      return
    }
    a.setAttribute('target', '_blank')
    const existingRel = a.getAttribute('rel') || ''
    const rel = new Set((existingRel.split(' ').filter(Boolean)))
    rel.add('noopener')
    rel.add('noreferrer')
    a.setAttribute('rel', Array.from(rel).join(' '))
  })
}
